import { useEffect, useRef, useState, type PointerEvent, type RefObject } from 'react';

import {
  useBoardDragPreviewStore,
  type WallBoard,
  type WallCard,
} from '@walls/store';
import { getCardPermissions } from '../../cards/hooks/useCardPermissions';
import { isCardVisibleForParticipantFilter } from '../../cards/utils/cardVisibility';
import { isClusterCard } from '../../selectors/boardCardSelectors';
import { moveChildIdBeforeTarget } from '../utils/clusterChildOrdering';
import {
  getClusterChildDragRect,
  getClusterChildDragSizes,
  getClusterDropTargetFromChildDrag,
  getDetachedCardPosition,
  getDraggedRectTargetChildId,
  isPointInElement,
} from '../utils/clusterChildDragGeometry';
import type { ClusterChildSortDrag } from '../types';
import type { useClusterCommands } from './useClusterCommands';

interface ClusterChildSortPointerEvent {
  clientX: number;
  clientY: number;
  pointerId: number;
  preventDefault: () => void;
  stopPropagation: () => void;
}

interface UseClusterChildSortingOptions {
  board: WallBoard;
  canSortClusterChildren: boolean;
  cards: WallCard[];
  cardsContainerRef: RefObject<HTMLDivElement | null>;
  cluster: WallCard;
  clusterCommands: ReturnType<typeof useClusterCommands>;
  currentParticipantId: string | null;
  elementRef: RefObject<HTMLElement | null>;
  isResizedCluster: boolean;
  orderedChildren: WallCard[];
  viewportRef: RefObject<HTMLElement | null> | null;
  visibleParticipantIds: string[];
  zoom: number;
}

export function useClusterChildSorting({
  board,
  canSortClusterChildren,
  cards,
  cardsContainerRef,
  cluster,
  clusterCommands,
  currentParticipantId,
  elementRef,
  isResizedCluster,
  orderedChildren,
  viewportRef,
  visibleParticipantIds,
  zoom,
}: UseClusterChildSortingOptions) {
  const childSortDragRef = useRef<ClusterChildSortDrag | null>(null);
  const stopChildSortWindowTrackingRef = useRef<(() => void) | null>(null);
  const [childSortDrag, setChildSortDrag] = useState<ClusterChildSortDrag | null>(null);
  const clusterPlacementPreview = useBoardDragPreviewStore((state) => state.clusterPlacement);
  const clearClusterPlacement = useBoardDragPreviewStore((state) => state.clearClusterPlacement);
  const setClusterPlacement = useBoardDragPreviewStore((state) => state.setClusterPlacement);

  const canSortChild = (childCard: WallCard) => {
    const childPermissions = getCardPermissions(board, childCard, currentParticipantId);

    return Boolean(
      canSortClusterChildren &&
        childPermissions.canMove &&
        !childCard.isLocked &&
        isCardVisibleForParticipantFilter(childCard, visibleParticipantIds)
    );
  };

  const stopChildSortWindowTracking = () => {
    stopChildSortWindowTrackingRef.current?.();
    stopChildSortWindowTrackingRef.current = null;
  };

  const releaseChildSortPointerCapture = (pointerId: number) => {
    const cardsContainer = cardsContainerRef.current;

    if (cardsContainer?.hasPointerCapture(pointerId)) {
      cardsContainer.releasePointerCapture(pointerId);
    }
  };

  const finishChildSortDrag = (pointerId?: number) => {
    const currentDrag = childSortDragRef.current;
    childSortDragRef.current = null;
    setChildSortDrag(null);
    clearClusterPlacement();
    stopChildSortWindowTracking();

    const pointerIdToRelease = pointerId ?? currentDrag?.pointerId;

    if (pointerIdToRelease != null) {
      releaseChildSortPointerCapture(pointerIdToRelease);
    }

    return currentDrag;
  };

  const handleChildSortPointerMove = (event: ClusterChildSortPointerEvent) => {
    const currentDrag = childSortDragRef.current;
    const cardsContainer = cardsContainerRef.current;

    if (!currentDrag || currentDrag.pointerId !== event.pointerId || !cardsContainer) {
      return;
    }

    if (!currentDrag.didDrag && Math.abs(event.clientY - currentDrag.pointerStartY) < 3) {
      return;
    }

    currentDrag.didDrag = true;
    currentDrag.pointerX = event.clientX;
    currentDrag.pointerY = event.clientY;
    currentDrag.isInsideSourceCluster = isPointInElement(
      event.clientX,
      event.clientY,
      elementRef.current
    );

    const draggedChildCard = orderedChildren.find(
      (childCard) => childCard.id === currentDrag.childId
    );
    const clusterDrop = draggedChildCard
      ? getClusterDropTargetFromChildDrag({
          board,
          card: draggedChildCard,
          cards,
          currentParticipantId,
          drag: currentDrag,
          viewport: viewportRef?.current ?? null,
          zoom,
        })
      : null;

    if (clusterDrop && !clusterDrop.createCluster && clusterDrop.cluster.id !== cluster.id) {
      currentDrag.placeholderBeforeChildId = null;
      setClusterPlacement({
        clusterId: clusterDrop.cluster.id,
        draggedCardId: clusterDrop.card.id,
        placeholderBeforeChildId: clusterDrop.placeholderBeforeChildId,
        size: clusterDrop.size,
      });
    } else if (currentDrag.isInsideSourceCluster) {
      clearClusterPlacement();
      currentDrag.placeholderBeforeChildId = getDraggedRectTargetChildId(
        getClusterChildDragRect(currentDrag),
        cardsContainer,
        currentDrag.childId,
        isResizedCluster
      );
    } else {
      clearClusterPlacement();
      currentDrag.placeholderBeforeChildId = null;
    }

    event.preventDefault();
    event.stopPropagation();
    setChildSortDrag({ ...currentDrag });
  };

  const handleChildSortPointerUp = (event: ClusterChildSortPointerEvent) => {
    const currentDrag = finishChildSortDrag(event.pointerId);

    if (!currentDrag || currentDrag.pointerId !== event.pointerId) {
      return;
    }

    if (!currentDrag.didDrag) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const childCard = orderedChildren.find(
      (currentChild) => currentChild.id === currentDrag.childId
    );

    if (childCard) {
      const clusterDrop = getClusterDropTargetFromChildDrag({
        board,
        card: childCard,
        cards,
        currentParticipantId,
        drag: currentDrag,
        viewport: viewportRef?.current ?? null,
        zoom,
      });

      if (clusterDrop && !clusterDrop.createCluster && clusterDrop.cluster.id !== cluster.id) {
        if (isClusterCard(childCard)) {
          void clusterCommands.addClusterToCluster(
            childCard,
            clusterDrop.cluster,
            clusterDrop.zPos
          );
        } else {
          void clusterCommands.addCardToCluster(childCard, clusterDrop.cluster, clusterDrop.zPos);
        }
        return;
      }
    }

    if (!isPointInElement(event.clientX, event.clientY, elementRef.current) && childCard) {
      const position = getDetachedCardPosition({
        clientX: event.clientX,
        clientY: event.clientY,
        drag: currentDrag,
        viewport: viewportRef?.current ?? null,
        zoom,
      });

      void clusterCommands.detachCardFromCluster(cluster, childCard, position.xPos, position.yPos);
      return;
    }

    const nextOrderedIds = moveChildIdBeforeTarget(
      orderedChildren.map((currentChild) => currentChild.id),
      currentDrag.childId,
      currentDrag.placeholderBeforeChildId
    );

    void clusterCommands.sortClusterChildren(cluster.id, nextOrderedIds);
  };

  const startChildSortWindowTracking = () => {
    stopChildSortWindowTracking();

    const onWindowPointerMove = (event: globalThis.PointerEvent) => {
      handleChildSortPointerMove(event);
    };
    const onWindowPointerUp = (event: globalThis.PointerEvent) => {
      handleChildSortPointerUp(event);
    };
    const onWindowPointerCancel = (event: globalThis.PointerEvent) => {
      finishChildSortDrag(event.pointerId);
    };

    window.addEventListener('pointermove', onWindowPointerMove, { passive: false });
    window.addEventListener('pointerup', onWindowPointerUp);
    window.addEventListener('pointercancel', onWindowPointerCancel);

    stopChildSortWindowTrackingRef.current = () => {
      window.removeEventListener('pointermove', onWindowPointerMove);
      window.removeEventListener('pointerup', onWindowPointerUp);
      window.removeEventListener('pointercancel', onWindowPointerCancel);
    };
  };

  useEffect(
    () => () => {
      const hadActiveChildSortDrag = Boolean(
        childSortDragRef.current || stopChildSortWindowTrackingRef.current
      );

      childSortDragRef.current = null;
      stopChildSortWindowTrackingRef.current?.();
      stopChildSortWindowTrackingRef.current = null;

      if (hadActiveChildSortDrag) {
        clearClusterPlacement();
      }
    },
    [clearClusterPlacement]
  );

  const getChildSortHandlers = (childCard: WallCard) => ({
    onPointerDownCapture: (event: PointerEvent<HTMLElement>) => {
      if (!canSortChild(childCard) || event.button !== 0) {
        return;
      }

      const cardsContainer = cardsContainerRef.current;

      if (!cardsContainer) {
        return;
      }

      clearClusterPlacement();
      startChildSortWindowTracking();
      cardsContainer.setPointerCapture(event.pointerId);
      const rect = event.currentTarget.getBoundingClientRect();
      const dragSizes = getClusterChildDragSizes(rect, zoom);
      childSortDragRef.current = {
        childId: childCard.id,
        didDrag: false,
        grabOffsetX: event.clientX - rect.left,
        grabOffsetY: event.clientY - rect.top,
        isInsideSourceCluster: true,
        placeholderBeforeChildId: childCard.id,
        pointerId: event.pointerId,
        pointerStartY: event.clientY,
        pointerX: event.clientX,
        pointerY: event.clientY,
        ...dragSizes,
      };
    },
  });

  const onCardsContainerPointerMove = (event: PointerEvent<HTMLElement>) => {
    handleChildSortPointerMove(event);
  };

  const onCardsContainerPointerUp = (event: PointerEvent<HTMLElement>) => {
    handleChildSortPointerUp(event);
  };

  const onCardsContainerPointerCancel = (event: PointerEvent<HTMLElement>) => {
    finishChildSortDrag(event.pointerId);
  };

  const onCardsContainerLostPointerCapture = (event: PointerEvent<HTMLElement>) => {
    const currentDrag = childSortDragRef.current;

    if (currentDrag?.pointerId === event.pointerId) {
      finishChildSortDrag(event.pointerId);
    }
  };

  const draggedChild = childSortDrag
    ? orderedChildren.find((childCard) => childCard.id === childSortDrag.childId)
    : null;
  const activeClusterPlacementPreview =
    !childSortDrag && clusterPlacementPreview?.clusterId === cluster.id
      ? clusterPlacementPreview
      : null;
  const showSourcePlacementPreview = Boolean(
    childSortDrag?.isInsideSourceCluster && !activeClusterPlacementPreview
  );

  return {
    activeClusterPlacementPreview,
    canSortChild,
    childSortDrag,
    draggedChild,
    getChildSortHandlers,
    showSourcePlacementPreview,
    cardsContainerPointerHandlers: {
      onLostPointerCapture: onCardsContainerLostPointerCapture,
      onPointerCancel: onCardsContainerPointerCancel,
      onPointerMove: onCardsContainerPointerMove,
      onPointerUp: onCardsContainerPointerUp,
    },
  };
}
